import React, { useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { CurrencyDollarIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import Toggle from '../../components/Toggle';
import {
  driverPayAdditionTypesService,
  type DriverPayAdditionTypeAdmin,
  type DriverPayAdditionTypeCreate,
  type DriverPayAdditionVariant,
} from '../../services/driverPayAdditionTypes.service';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

/** Catalogue of driver-pay addition types used by the Pro detail Addl Cost
 *  modal. Rows here drive both the dropdown shown in that modal and the
 *  form variant / conditional fields the modal renders for each type. */

type EditState =
  | { kind: 'closed' }
  | { kind: 'new' }
  | { kind: 'edit'; row: DriverPayAdditionTypeAdmin };

const VARIANTS: DriverPayAdditionVariant[] = ['drayage', 'waiting', 'default'];

const emptyForm: DriverPayAdditionTypeCreate = {
  name: '',
  variant: 'default',
  showsFuelType: false,
  gwiCommission: true,
  limitAmount: 0,
  managerOnly: false,
  description: '',
  isActive: true,
};

const DriverPayAdditionTypes: React.FC = () => {
  const [rows, setRows] = useState<DriverPayAdditionTypeAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState>({ kind: 'closed' });
  const [showInactive, setShowInactive] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await driverPayAdditionTypesService.list();
      setRows(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load addition types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const visibleRows = useMemo(
    () => (showInactive ? rows : rows.filter((r) => r.isActive)),
    [rows, showInactive],
  );

  const columns = useMemo<ColumnDef<DriverPayAdditionTypeAdmin>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 240,
        cell: ({ row }) => (
          <span className="font-medium text-body">{row.original.name}</span>
        ),
      },
      {
        accessorKey: 'variant',
        header: 'Variant',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        cell: ({ row }) => <span className="lowercase">{row.original.variant}</span>,
      },
      {
        id: 'showsFuelType',
        accessorFn: (r) => (r.showsFuelType ? 'Yes' : 'No'),
        header: 'Has Fuel',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 110,
        cell: ({ row }) => (
          <span className="block text-center">{row.original.showsFuelType ? '✓' : ''}</span>
        ),
      },
      {
        id: 'gwiCommission',
        accessorFn: (r) => (r.gwiCommission ? 'Yes' : 'No'),
        header: 'GWI Commission',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 130,
        cell: ({ row }) => (
          <span className="block text-center">{row.original.gwiCommission ? '✓' : ''}</span>
        ),
      },
      {
        accessorKey: 'limitAmount',
        header: 'Limit',
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
        cell: ({ row }) => (
          <span className="block text-right">
            {row.original.limitAmount > 0 ? row.original.limitAmount.toFixed(2) : '—'}
          </span>
        ),
      },
      {
        id: 'managerOnly',
        accessorFn: (r) => (r.managerOnly ? 'Yes' : 'No'),
        header: 'Manager Only',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        cell: ({ row }) => (
          <span className="block text-center">{row.original.managerOnly ? '✓' : ''}</span>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        enableSorting: false,
        enableColumnFilter: true,
        size: 320,
        cell: ({ row }) => (
          <span
            className="block truncate text-gray-600 dark:text-gray-300"
            title={row.original.description}
          >
            {row.original.description || '—'}
          </span>
        ),
      },
      {
        id: 'isActive',
        accessorFn: (r) => (r.isActive ? 'Yes' : 'No'),
        header: 'Active',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 90,
        cell: ({ row }) => (
          <span className="block text-center">{row.original.isActive ? 'Yes' : 'No'}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        enableColumnFilter: false,
        size: 56,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setEdit({ kind: 'edit', row: row.original });
              }}
              className="text-indigo-600 hover:text-indigo-700"
              aria-label={`Edit ${row.original.name}`}
            >
              <PencilSquareIcon className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="h-full w-full p-6">
      <div className="mb-4 flex items-start gap-3">
        <CurrencyDollarIcon className="h-6 w-6 flex-shrink-0 text-gray-500" />
        <div>
          <h1 className="text-xl font-semibold text-heading">
            Driver Pay Addition Types
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-muted">
            These rows drive the Type dropdown on the Pro detail “Addl Cost” modal and tell that
            modal which form variant to render. Inactive types are hidden from the dropdown but
            preserved so legacy line items still resolve to a row.
          </p>
        </div>
      </div>

      <TanTable
        data={visibleRows}
        columns={columns}
        loading={loading}
        errorMessage={error}
        onRetry={load}
        settingsKey="driver-pay-addition-types-table"
        fieldName="driver-pay-addition-types-table"
        searchPlaceholder="Search addition types..."
        cardTitle="Addition Types"
        enableColumnFilters
        onRowClick={(r) => setEdit({ kind: 'edit', row: r })}
        getRowClassName={(r) => (r.isActive ? '' : 'opacity-60')}
        onEditFilters={() => setEdit({ kind: 'new' })}
        showEditFiltersButton
        editFiltersButtonIcon="plus"
        editFiltersButtonTooltip="Add type"
        headerExtra={
          <label className="flex items-center gap-2 whitespace-nowrap text-sm text-label">
            <Toggle
              checked={showInactive}
              onChange={setShowInactive}
              size="sm"
              ariaLabel="Show inactive types"
            />
            Show inactive
          </label>
        }
      />

      {edit.kind !== 'closed' && (
        <EditModal
          initial={edit.kind === 'edit' ? toForm(edit.row) : emptyForm}
          recordId={edit.kind === 'edit' ? edit.row.id : null}
          onClose={() => setEdit({ kind: 'closed' })}
          onSaved={async () => {
            setEdit({ kind: 'closed' });
            await load();
          }}
        />
      )}
    </div>
  );
};

const toForm = (r: DriverPayAdditionTypeAdmin): DriverPayAdditionTypeCreate => ({
  name: r.name,
  variant: r.variant,
  showsFuelType: r.showsFuelType,
  gwiCommission: r.gwiCommission,
  limitAmount: r.limitAmount,
  managerOnly: r.managerOnly,
  description: r.description,
  isActive: r.isActive,
});

// ---------------------------------------------------------------------------
// Edit modal
// ---------------------------------------------------------------------------

interface EditModalProps {
  initial: DriverPayAdditionTypeCreate;
  recordId: number | null;
  onClose: () => void;
  onSaved: () => void;
}

const EditModal: React.FC<EditModalProps> = ({ initial, recordId, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  const field = <K extends keyof DriverPayAdditionTypeCreate>(
    key: K,
    value: DriverPayAdditionTypeCreate[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (recordId == null) {
        await driverPayAdditionTypesService.create(form);
      } else {
        await driverPayAdditionTypesService.update(recordId, form);
      }
      onSaved();
    } catch (err: unknown) {
      const detail =
        typeof err === 'object' && err && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      setError(detail || (err instanceof Error ? err.message : 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (recordId == null) return;
    if (!window.confirm('Deactivate this addition type? It will be hidden from the modal.')) return;
    setSaving(true);
    setError(null);
    try {
      await driverPayAdditionTypesService.deactivate(recordId);
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onClose} />
        <div
          ref={panelRef}
          style={panelStyle}
          className="relative bg-panel rounded-lg shadow-xl max-w-md w-full"
        >
          <ModalHeader
            title={recordId == null ? 'New Addition Type' : 'Edit Addition Type'}
            onClose={onClose}
            dragHandleProps={dragHandleProps}
          />

          <div className="px-4 py-3 space-y-3">
            {error && (
              <div className="px-2 py-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded">
                {error}
              </div>
            )}

            <Field label="Name">
              <input
                className={inputClass}
                value={form.name}
                onChange={(e) => field('name', e.target.value)}
                onBlur={(e) => field('name', e.target.value.toUpperCase())}
                placeholder="e.g. ADDITIONAL DRAYAGE"
                disabled={recordId != null}
              />
              {recordId != null && (
                <p className="mt-1 text-[0.6875rem] text-gray-500">
                  Name is locked to keep existing driverpay_lineitem rows resolvable.
                </p>
              )}
            </Field>

            <Field label="Variant">
              <select
                className={inputClass}
                value={form.variant}
                onChange={(e) => field('variant', e.target.value as DriverPayAdditionVariant)}
              >
                {VARIANTS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>

            <FlagRow
              label="Has Fuel"
              checked={form.showsFuelType}
              onChange={(v) => field('showsFuelType', v)}
            />
            <FlagRow
              label="GWI Commission"
              checked={form.gwiCommission}
              onChange={(v) => field('gwiCommission', v)}
            />
            <FlagRow
              label="Manager Only"
              checked={form.managerOnly}
              onChange={(v) => field('managerOnly', v)}
            />

            <Field label="Limit (0 = no limit)">
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputClass}
                value={form.limitAmount}
                onChange={(e) => field('limitAmount', parseFloat(e.target.value) || 0)}
              />
            </Field>

            <Field label="Description">
              <textarea
                className={`${inputClass} h-auto py-1.5`}
                rows={3}
                maxLength={500}
                value={form.description}
                onChange={(e) => field('description', e.target.value)}
                placeholder="Shown under the Type dropdown on the Pro Addl Cost modal"
              />
            </Field>

            <FlagRow
              label="Active"
              checked={form.isActive}
              onChange={(v) => field('isActive', v)}
            />
          </div>

          <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-700 flex items-center justify-end gap-2">
            {recordId != null && form.isActive && (
              <button
                type="button"
                onClick={handleDeactivate}
                disabled={saving}
                className="mr-auto px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-50"
              >
                Deactivate
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-medium text-label bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50 dark:bg-gray-700 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-semibold text-white bg-indigo-600 border border-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const inputClass =
  'w-full h-8 px-2 text-sm rounded border border-slate-300 bg-white text-slate-900 ' +
  'focus:outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 disabled:bg-slate-100 disabled:text-slate-500 ' +
  'dark:bg-gray-700 dark:text-white dark:border-slate-600 dark:disabled:bg-gray-600';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 mb-1">
      {label}
    </label>
    {children}
  </div>
);

const FlagRow: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({
  label,
  checked,
  onChange,
}) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-gray-800 dark:text-gray-200">{label}</span>
    <Toggle checked={checked} onChange={onChange} size="sm" ariaLabel={label} />
  </div>
);

export default DriverPayAdditionTypes;
